import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { useAuth } from "../App";
import { NAV_LABELS } from "../constants/uiCopy";

const Footer = () => {
  const { user } = useAuth();

  return (
    <footer
      className="bg-foreground text-background py-10 sm:py-12 md:py-14"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-7 lg:gap-8">
          {/* Brand */}
          <div>
            <h3
              className="text-base sm:text-lg font-bold mb-3"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Metal Concrete
            </h3>
            <p className="text-xs sm:text-sm text-background/70 mb-3 leading-relaxed">
              Global Real Estate Network
            </p>
            <p className="text-xs text-background/60 leading-relaxed">
              SUIVOS, S.A. de C.V.
            </p>
          </div>

          {/* Marketplace */}
          <div>
            <h4 className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wider">
              Marketplace
            </h4>
            <ul className="space-y-1.5 text-xs sm:text-sm text-background/70">
              <li>
                <Link
                  to="/search"
                  className="hover:text-background transition-colors duration-200"
                >
                  Buscar
                </Link>
              </li>
              <li>
                <Link
                  to="/search?category=Residencial"
                  className="hover:text-background transition-colors duration-200"
                >
                  Residencial
                </Link>
              </li>
              <li>
                <Link
                  to="/search?category=Comercial"
                  className="hover:text-background transition-colors duration-200"
                >
                  Comercial
                </Link>
              </li>
              <li>
                <Link
                  to="/search?category=Industrial"
                  className="hover:text-background transition-colors duration-200"
                >
                  Industrial
                </Link>
              </li>
              <li>
                <Link
                  to="/developments-list"
                  className="hover:text-background transition-colors duration-200"
                >
                  Desarrollos
                </Link>
              </li>
              <li>
                <Link
                  to="/brokers-directory"
                  className="hover:text-background transition-colors duration-200"
                >
                  {NAV_LABELS.brokersDirectory}
                </Link>
              </li>
              <li>
                <Link
                  to="/inventario-brokers"
                  className="hover:text-background transition-colors duration-200"
                >
                  {NAV_LABELS.collaborationInventory}
                </Link>
              </li>
              <li>
                <Link
                  to="/property-requests"
                  className="hover:text-background transition-colors duration-200"
                >
                  Búsquedas Activas
                </Link>
              </li>
            </ul>
          </div>

          {/* Planes */}
          <div>
            <h4 className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wider">
              Planes
            </h4>
            <ul className="space-y-1.5 text-xs sm:text-sm text-background/70">
              <li>
                <Link
                  to="/pricing"
                  className="hover:text-background transition-colors duration-200"
                >
                  {NAV_LABELS.pricing}
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing#planes"
                  className="hover:text-background transition-colors duration-200"
                >
                  {NAV_LABELS.pricingTable}
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing#faqs"
                  className="hover:text-background transition-colors duration-200"
                >
                  {NAV_LABELS.pricingFaq}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wider">
              Legal
            </h4>
            <ul className="space-y-1.5 text-xs sm:text-sm text-background/70">
              <li>
                <Link
                  to="/about"
                  className="hover:text-background transition-colors duration-200"
                >
                  Acerca de
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="hover:text-background transition-colors duration-200"
                >
                  Aviso de Privacidad
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="hover:text-background transition-colors duration-200"
                >
                  Términos y Condiciones
                </Link>
              </li>
            </ul>
          </div>

          {/* Cuenta */}
          <div>
            <h4 className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wider">
              Cuenta
            </h4>
            <ul className="space-y-1.5 text-xs sm:text-sm text-background/70">
              <li>
                <Link
                  to={user ? "/dashboard" : "/login"}
                  className="hover:text-background transition-colors duration-200"
                >
                  {user ? "Dashboard" : "Iniciar Sesión"}
                </Link>
              </li>
              <li>
                <Link
                  to={user ? "/account" : "/register"}
                  className="hover:text-background transition-colors duration-200"
                >
                  {user ? "Mi Cuenta" : "Registrarse"}
                </Link>
              </li>
              {user && (
                <li>
                  <Link
                    to="/account?tab=subscription"
                    className="hover:text-background transition-colors duration-200"
                  >
                    Suscripción
                  </Link>
                </li>
              )}
              {user && (
                <>
                  <li>
                    <Link
                      to="/favorites"
                      className="hover:text-background transition-colors duration-200"
                    >
                      Favoritos
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/inquiries"
                      className="hover:text-background transition-colors duration-200"
                    >
                      Consultas
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/property-requests"
                      className="hover:text-background transition-colors duration-200"
                    >
                      Búsquedas Activas
                    </Link>
                  </li>
                  {(user?.role === "basic" ||
                    user?.role === "owner" ||
                    user?.role === "broker" ||
                    user?.role === "developer") && (
                    <li>
                      <Link
                        to="/add-property"
                        className="hover:text-background transition-colors duration-200"
                      >
                        Nueva Propiedad
                      </Link>
                    </li>
                  )}
                  {user?.role === "developer" && (
                    <li>
                      <Link
                        to="/add-development"
                        className="hover:text-background transition-colors duration-200"
                      >
                        Nuevo Desarrollo
                      </Link>
                    </li>
                  )}
                  {user?.role === "broker" && (
                    <li>
                      <Link
                        to="/broker-profile/edit"
                        className="hover:text-background transition-colors duration-200"
                      >
                        Editar Perfil Broker
                      </Link>
                    </li>
                  )}
                  {user?.role === "developer" && (
                    <li>
                      <Link
                        to="/developer-profile/edit"
                        className="hover:text-background transition-colors duration-200"
                      >
                        Editar Perfil Desarrollador
                      </Link>
                    </li>
                  )}
                  {user?.role === "admin" && (
                    <>
                      <li>
                        <Link
                          to="/admin/users"
                          className="hover:text-background transition-colors duration-200"
                        >
                          Gestión de Usuarios
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/brokers"
                          className="hover:text-background transition-colors duration-200"
                        >
                          Gestión de Brokers
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/settings"
                          className="hover:text-background transition-colors duration-200"
                        >
                          Configuración Admin
                        </Link>
                      </li>
                    </>
                  )}
                </>
              )}
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h4 className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wider">
              Contacto
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-background/70">
              <li className="flex items-start space-x-2 min-w-0">
                <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="min-w-0 break-words leading-relaxed">
                  soporte@metalconcrete.com
                </span>
              </li>
              <li className="flex items-start space-x-2 min-w-0">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="min-w-0 break-words leading-relaxed">
                  Monterrey, MX | Saltillo, MX
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-background/20 text-center text-xs sm:text-sm text-background/60">
          <p>
            &copy; 2026 Metal Concrete &middot; Global Real Estate Network &middot; SUIVOS, S.A. de C.V. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
